import type { ReactNode } from "react";
import type { IconName } from "../icons";
import { cn } from "../lib/cn";
import { StatusBadge, type StatusKind } from "./badge";
import { Icon } from "./icon";
import { Progress } from "./progress";

export const INGESTION_STAGES = ["uploading", "parsing", "chunking", "contextualizing", "embedding", "ready"] as const;
export type IngestionStage = (typeof INGESTION_STAGES)[number] | "failed";

export const INGESTION_STAGE_LABELS: Record<IngestionStage, string> = {
  uploading: "Uploading",
  parsing: "Parsing",
  chunking: "Chunking",
  contextualizing: "Contextualizing",
  embedding: "Embedding",
  ready: "Ready",
  failed: "Failed",
};

export interface IngestionItem {
  id: string;
  name: string;
  stage: IngestionStage;
  /** 0–100 within the current stage (e.g. upload bytes). Omit for stage-step progress. */
  progress?: number;
  /** Friendly failure reason (never a raw provider error). */
  error?: string;
}

/** Overall percent: completed stages + fraction of the current stage. */
export function ingestionPercent(item: Pick<IngestionItem, "stage" | "progress">): number {
  if (item.stage === "ready") return 100;
  if (item.stage === "failed") return 100;
  const steps = INGESTION_STAGES.length - 1;
  const index = INGESTION_STAGES.indexOf(item.stage);
  const within = Math.min(Math.max(item.progress ?? 0, 0), 100) / 100;
  return Math.round(((index + within) / steps) * 100);
}

function fileIcon(name: string): IconName {
  if (/\.(xlsx|csv)$/i.test(name)) return "file-spreadsheet";
  if (/\.(jpe?g|png)$/i.test(name)) return "image";
  return "file-text";
}

function statusOf(stage: IngestionStage): StatusKind {
  if (stage === "ready") return "success";
  if (stage === "failed") return "danger";
  return "info";
}

const STAGE_ICON: Partial<Record<IngestionStage, IconName>> = { ready: "check-circle", failed: "x-circle" };

export interface IngestionRowProps {
  item: IngestionItem;
  labels?: Partial<Record<IngestionStage, string>>;
  /** Shown beside the failure reason (e.g. a retry button). */
  action?: ReactNode;
  className?: string;
}

/** Upload / processing row: file icon, name, stage badge (icon + text), progress bar, failure reason. */
export function IngestionRow({ item, labels, action, className }: IngestionRowProps) {
  const stageLabel = labels?.[item.stage] ?? INGESTION_STAGE_LABELS[item.stage];
  const pct = ingestionPercent(item);
  const failed = item.stage === "failed";
  return (
    <li className={cn("flex items-center gap-3 rounded-md border border-line bg-surface px-3 py-2.5 animate-rise motion-reduce:animate-none", className)}>
      <Icon name={fileIcon(item.name)} className="size-5 text-primary-ink" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold text-fg">{item.name}</span>
          <StatusBadge status={statusOf(item.stage)} label={stageLabel} icon={STAGE_ICON[item.stage] ?? "refresh-cw"} />
        </div>
        {failed ? (
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-danger">{item.error ?? "Processing failed"}</p>
            {action}
          </div>
        ) : (
          <Progress
            className="mt-1.5"
            size="sm"
            value={pct}
            label={`${item.name}: ${stageLabel}`}
            valueText={`${pct}%, ${stageLabel}`}
            tone={item.stage === "ready" ? "success" : "primary"}
          />
        )}
      </div>
    </li>
  );
}

export interface IngestionListProps {
  items: readonly IngestionItem[];
  "aria-label": string;
  labels?: Partial<Record<IngestionStage, string>>;
  className?: string;
}

/** Data-driven list (fed by upload progress + `GET /v1/sources/events`). No simulation. */
export function IngestionList({ items, labels, className, ...rest }: IngestionListProps) {
  if (items.length === 0) return null;
  return (
    <ul aria-label={rest["aria-label"]} className={cn("space-y-2", className)}>
      {items.map((item) => (
        <IngestionRow key={item.id} item={item} labels={labels} />
      ))}
    </ul>
  );
}
